import { Permissions } from '@/app/types';
import { errorNameOf } from '@/app/utils/errors';
import { readCapturePermissionState } from '@/app/utils/permissions';
import SentryImportPromise from '@/sentry/importPromise';

export type CaptureTrigger = 'mount' | 'chip' | 'join' | 'retry';
export type CaptureOutcome = 'granted' | 'refused' | 'joined-without';

/**
 * Filed as a message rather than an exception: a refusal is the customer's
 * answer, not a defect, and the browser's own verdict beside it is what tells
 * a session-level "Don't Allow" (prompt) from a block in the site settings
 * (denied).
 */
export async function reportCaptureOutcome(
  trigger: CaptureTrigger,
  outcome: CaptureOutcome,
  requested: Permissions,
  error?: unknown,
) {
  const [{ default: Sentry }, microphonePermission, cameraPermission] = await Promise.all([
    SentryImportPromise,
    readCapturePermissionState('microphone'),
    readCapturePermissionState('camera'),
  ]);
  const errorName = errorNameOf(error);
  const tags: Record<string, string> = { operation: 'lobby-capture', trigger, outcome };
  const fingerprint = ['lobby-capture', trigger, outcome];
  if (errorName) {
    tags.errorName = errorName;
    fingerprint.push(errorName);
  }

  Sentry.captureMessage(`Lobby capture ${outcome} on ${trigger}`, {
    level: 'info',
    tags,
    fingerprint,
    extra: {
      requested,
      microphonePermission,
      cameraPermission,
      errorMessage: error instanceof Error ? error.message : undefined,
    },
  });
}
